import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BelowThresholdAction,
  BelowThresholdActionOption,
  ConfidenceThreshold,
} from './types';
import Cols from '__components/Layout/Cols';
import Select from '__components/Inputs/Select';
import { getBelowThresholdActionOptions } from './helpers';
import { StyledRangeInput, StyledRangeLabels } from './styles';
import InputControl from '__components/Inputs/InputControl';
import { KizenTypography } from '__app/typography';

const MIN_CONFIDENCE = 0.1;
const MAX_CONFIDENCE = 0.9;

export type ChangeConfidenceData = {
  confidenceThreshold: ConfidenceThreshold;
  belowThresholdAction: BelowThresholdAction;
};

type LLMConfidenceThresholdProps = {
  onChange: (payload: ChangeConfidenceData) => void;
  confidenceThreshold: ConfidenceThreshold;
  belowThresholdAction: BelowThresholdAction;
  disabled: boolean;
};

export const LLMConfidenceThreshold: FC<LLMConfidenceThresholdProps> = ({
  confidenceThreshold,
  belowThresholdAction,
  onChange,
  disabled,
}) => {
  const { t } = useTranslation();

  const belowThresholdActionOptions = useMemo(
    () => getBelowThresholdActionOptions(t),
    [t]
  );

  const onChangeThreshold = (value: ConfidenceThreshold) => {
    onChange({
      confidenceThreshold: value,
      belowThresholdAction,
    });
  };

  const onChangeAction = ({ value }: BelowThresholdActionOption) => {
    onChange({
      confidenceThreshold,
      belowThresholdAction: value,
    });
  };

  return (
    <>
      <InputControl
        label={t('Decision Confidence Threshold')}
        labelInfo={t(
          'Sets how confident the AI must be to make automatic decisions. Higher settings mean fewer automatic decisions but greater accuracy. Lower settings automate more but may require additional review.'
        )}
        labelInfoPlacement="top"
        margin
      >
        <StyledRangeInput
          value={confidenceThreshold ?? (MIN_CONFIDENCE + MAX_CONFIDENCE) / 2}
          min={MIN_CONFIDENCE}
          max={MAX_CONFIDENCE}
          step={0.05}
          withSteps
          onChange={onChangeThreshold}
          disabled={disabled}
          showTooltip={false}
        />
        <StyledRangeLabels>
          {[
            t(
              'I want to automate most decisions, knowing they may be less accurate'
            ),
            t("I'd like a balance between agentic workflow and manual reviews"),
            t(
              'I want to review more decisions myself, automating only when very certain'
            ),
          ].map((label, i) => (
            <KizenTypography key={i}>{label}</KizenTypography>
          ))}
        </StyledRangeLabels>
      </InputControl>

      <Cols columns={3} gutter="20px">
        <Select
          label={t('Low Confidence Action')}
          value={belowThresholdAction}
          options={belowThresholdActionOptions}
          onChange={onChangeAction}
          menuInline
          margin
          disabled={disabled}
          captureMenuScroll={false}
        />
      </Cols>
    </>
  );
};
